/* eslint-disable no-fallthrough */
import type {
  Contact,
  Contribution,
  Student,
  APIContact,
  APIContribution,
  APIStudent,
  APIClassroom,
  APIGoal,
  Classroom,
  Goal,
  Status,
} from '@/services/contribution/contribution.d.ts'

import { isDefined } from '@/util/helpers'

import type {
  LoginSessionResponse,
  CreateContactErrorResult,
  CreateContactApiError,
  CreateContactApiResponse,
  CreateContactResult,
  CreateContactErrorResultContact,
} from './api.tsx'

export function apiTransformSession(session: LoginSessionResponse) {
  return {
    accessToken: session.access_token,
    refreshToken: session.refresh_token,
    eventId: session.event_id,
    participationId: session.participation_id,
    parentName: session.parent_name ?? '',
    parentSlug: session.parent_slug,
    schoolName: session.school_name,
    photoUrl: session.photo_url,
    isParticipationMutable: session.participation_editable,
  }
}

export function apiTransformGoal(goal: APIGoal): Goal {
  switch (goal.strategy) {
    case 'static':
      return {
        target: goal.target,
        goals: [goal.target],
        progress: goal.progress,
      }

    case 'floating':
      return {
        target: goal.current_target, // only show the next goal, not the original goal
        goals: [goal.current_target],
        progress: goal.progress,
      }

    case 'milestone':
      return {
        target: goal.base_target,
        goals: [goal.base_target, ...goal.milestones],
        progress: goal.progress,
      }

    default: {
      const exhaustiveCheck: never = goal
      throw new Error(`Unrecognized goal strategy: ${JSON.stringify(exhaustiveCheck)}`)
    }
  }
}

export function apiTransformContacts(apiContacts: APIContact[]): Contact[] {
  return apiContacts
    .map((c) => {
      if (!c || !c.id) return undefined

      return {
        id: c.id,
        name: c.name ?? '',
        alias: c.alias ?? '',
        email: c.email ?? '',
        phone: c.phone ? `+${c.phone}` : '',
        emailStatus: apiTransformStatus(c.email_status ?? ''),
        smsStatus: apiTransformStatus(c.sms_status ?? ''),
      } as Contact
    })
    .filter(isDefined)
}

export function apiTransformCreateContactResponse(response: CreateContactApiResponse): CreateContactResult {
  return {
    contactGoal: response.contact_goal,
    goals: {
      contactGoal: apiTransformGoal(response.goals.contact_goal),
    },
    contacts: apiTransformContacts(response.contacts),
    errors: response.errors ? response.errors.map((e) => apiTransformCreateContactErrorResponse(e)) : undefined,
  }
}

export function apiTransformCreateContactErrorResponse(error: CreateContactApiError): CreateContactErrorResult {
  return {
    contact: {
      id: error.contact.id ?? null,
      parentId: error.contact.parent_id ?? null,
      name: error.contact.name ?? null,
      email: error.contact.email ?? null,
      alias: error.contact.alias ?? null,
      language: error.contact.language ?? null,
      createdAt: error.contact.created_at ?? null,
      updatedAt: error.contact.updated_at ?? null,
      donationRemindersCount: error.contact.donation_reminders_count ?? null,
      isParent: error.contact.is_parent ?? null,
      emailStatus: error.contact.email_status ?? null,
      smsStatus: error.contact.sms_status ?? null,
      participationId: error.contact.participation_id ?? null,
      optOutAt: error.contact.opt_out_at ?? null,
      phoneId: error.contact.phone_id ?? null,
      tributaryFlow: error.contact.tributary_flow ?? null,
    } as CreateContactErrorResultContact,

    errors: error.errors ?? [],
  }
}

function apiTransformStatus(status: string): Status {
  if (!status || status.trim() === '') {
    return 'na'
  }

  const normalizedStatus = status.toLowerCase().trim()

  switch (normalizedStatus) {
    case 'pending':
      return 'pending'

    // Success statuses - Email
    case 'delivered':
    case 'open':
    case 'click':
    case 'processed':
    case 'sent_manually':
    case 'link_opened':

    // Success statuses - SMS
    case 'accepted':
    case 'queued':
    case 'sending':
    case 'sent':
    case 'receiving':
    case 'received':
      return 'success'

    // Email failure statuses that allow the email to be edited
    case 'bounce':
    case 'dropped':
    case 'zb_invalid':
      return 'failure-editable-email'

    // SMS Failure statuses that allow the phone number to be edited
    case 'undelivered':
    case 'undeliverable':
    case 'failed':
      return 'failure-editable-phone'

    case 'spamreport':
    case 'unsubscribe':
    case 'group_unsubscribe':

    // Failure statuses - SMS
    case 'unsubscribed':
      return 'failure'

    default:
      return 'na'
  }
}

export function apiTransformContributions(apiContributions: APIContribution[]): Contribution[] {
  return apiContributions
    .map((c) => {
      if (!c || !c.id) return undefined

      const splits = c.id.split('_')

      return {
        id: splits[splits.length - 1],
        name: c.name ?? '',
        amount: c.amount ?? '',
        type: splits[0] === 'offline' ? 'offline' : 'online',
        createdAt: c.created_at ?? '',
      } as Contribution
    })
    .filter(isDefined)
}

export function apiTransformClassrooms(apiClassrooms: Array<APIClassroom | null>): Classroom[] {
  return apiClassrooms
    .map((c) => {
      if (!c || !c.id)
        return {
          id: null,
          grade: '',
          name: 'Other',
        } as Classroom

      return {
        id: c.id,
        grade: c.grade ?? '',
        name: c.name ?? '',
      } as Classroom
    })
    .filter(isDefined)
}

export function apiTransformStudents(apiStudents: APIStudent[]): Student[] {
  return apiStudents
    .map((s) => {
      if (!s || !s.id) return undefined

      const classroom = apiTransformClassrooms([s.classroom])[0]

      return {
        id: s.id,
        firstName: s.first_name ?? '',
        lastName: s.last_name ?? '',
        readingLevel: s.grade ?? '',
        classroom,
      } as Student
    })
    .filter(isDefined)
}
